<script setup lang="ts">
/**
 * Modal dialog with adaptive presentation:
 *
 * - **mobile** (< md): bottom sheet anchored to the bottom edge,
 *   full-width, slides up. Respects iOS safe-area inset.
 * - **desktop** (≥ md): centred floating panel with max-width.
 *
 * Pass `variant="centered"` to force the desktop layout on mobile too
 * (e.g. for confirm prompts where the sheet feels heavy).
 *
 * Focus management, scroll lock, and Esc handling come from Reka.
 *
 * **When NOT to use:**
 * - Routed pages — full screens belong in `router/index.ts` with a
 *   real URL. Dialogs are ephemeral and don't survive a refresh.
 * - Persistent overlays like the offline banner — use
 *   `AppNetworkBanner`, which mounts once globally and doesn't trap
 *   focus.
 * - Per-row inline confirmations (e.g. trash a single dish) — prefer
 *   a two-step button (arm → confirm) inside the row; spawning a
 *   dialog per row is heavyweight and breaks keyboard nav.
 * - A fourth nested layer on top of `level="deep"` — that's a sign the
 *   flow is too deep; consider a wizard / multi-step inside the
 *   existing dialog.
 */
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from 'reka-ui'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { X } from '@lucide/vue'

import { cn } from '@/shared/lib/cn'

interface Props {
  open?: boolean
  title?: string
  description?: string
  /** `sheet`: bottom-sheet on mobile, centred on desktop (default).
   *  `centered`: always centred. */
  variant?: 'sheet' | 'centered'
  /** Max width on desktop. `md` (default) = max-w-lg; `lg` = max-w-3xl
   *  for content that needs room (e.g. a split media browser). */
  size?: 'md' | 'lg'
  /**
   * Whether the user can dismiss the dialog. `true` (default) shows the
   * X button and lets Esc / backdrop close it. `false` hides X and
   * blocks Esc/backdrop — use it while a save is in flight so the user
   * can't accidentally close mid-request (the mutation would still
   * resolve server-side, but the toast would land on a stale screen).
   */
  closable?: boolean
  /** Hide title visually but keep it for screen readers. */
  titleVisuallyHidden?: boolean
  /**
   * Stacking level for nested dialogs. A dialog opened from *inside*
   * another must sit above it — otherwise its overlay renders at the
   * same depth and the two panels visually merge (and the inner one can
   * end up *behind* the blur).
   *
   * - `base` (default): z-40/50 — top-level dialogs.
   * - `nested`: z-[52]/[54] — a dialog inside a base dialog.
   * - `deep`: z-[56]/[58] — a dialog inside a nested dialog (e.g. the
   *   shared-mod editor reached from the manage-group panel, itself
   *   reached from the item editor).
   *
   * All stay below the global toast (z-[60]) and network banner
   * (z-[70]) so feedback always wins.
   */
  level?: 'base' | 'nested' | 'deep'
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  title: undefined,
  description: undefined,
  variant: 'sheet',
  size: 'md',
  closable: true,
  titleVisuallyHidden: false,
  level: 'base',
})

const emit = defineEmits<{ 'update:open': [open: boolean] }>()

// --- sticky-header / sticky-footer edge sensing ----------------------
//
// We want the divider line under the header (and above the footer) to
// appear only when there's actually content scrolled past them — on a
// short dialog there's nothing to separate. Each "edge" is detected by
// a zero-height sentinel placed where the body meets the bar; an
// IntersectionObserver watches whether the sentinel is in view of the
// scroll container. When it leaves the viewport, we know the bar is
// covering content and flip the divider on.
//
// One observer per edge keeps the per-frame work tiny; created lazily
// on mount and torn down when the dialog closes.
const topSentinelRef = ref<HTMLElement | null>(null)
const bottomSentinelRef = ref<HTMLElement | null>(null)
const headerScrolled = ref(false)
const footerScrolled = ref(false)

let topObserver: IntersectionObserver | null = null
let bottomObserver: IntersectionObserver | null = null

function teardownObservers() {
  topObserver?.disconnect()
  topObserver = null
  bottomObserver?.disconnect()
  bottomObserver = null
  headerScrolled.value = false
  footerScrolled.value = false
}

/**
 * The sentinels scroll inside DialogContent (its `overflow-auto`), not
 * the viewport. Observing against the viewport (root: null) flips the
 * divider late/inaccurately — the dialog fills most of the screen, so a
 * sentinel can be hidden under the sticky header yet still "intersect"
 * the viewport. Walk up to the nearest scrollable ancestor and use it
 * as the observer root. Falls back to `null` (viewport) if none is
 * found — i.e. never worse than the previous behaviour.
 */
function scrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement
  while (node) {
    const oy = getComputedStyle(node).overflowY
    if (oy === 'auto' || oy === 'scroll') return node
    node = node.parentElement
  }
  return null
}

// Reka mounts DialogContent into a portal; the sentinels exist as soon
// as `open` flips true and the template renders. Watch `open` so we
// re-observe each time the dialog reopens (sentinels remount).
watch(
  () => props.open,
  async (open) => {
    if (!open) {
      teardownObservers()
      return
    }
    // Wait two ticks: first for v-if to mount the portal content, then
    // for the sentinels' refs to be populated.
    await Promise.resolve()
    await Promise.resolve()
    if (typeof IntersectionObserver === 'undefined') return

    if (topSentinelRef.value) {
      const root = scrollParent(topSentinelRef.value)
      topObserver = new IntersectionObserver(
        ([entry]) => {
          headerScrolled.value = !!entry && !entry.isIntersecting
        },
        { root, threshold: 0 },
      )
      topObserver.observe(topSentinelRef.value)
    }
    if (bottomSentinelRef.value) {
      const root = scrollParent(bottomSentinelRef.value)
      bottomObserver = new IntersectionObserver(
        ([entry]) => {
          footerScrolled.value = !!entry && !entry.isIntersecting
        },
        { root, threshold: 0 },
      )
      bottomObserver.observe(bottomSentinelRef.value)
    }
  },
  { immediate: true },
)

onBeforeUnmount(teardownObservers)

// Full literal classes (no interpolation) so Tailwind's scanner picks
// them up. `lg` widens the centred panel for content that needs room.
const centeredMaxW = computed(() => (props.size === 'lg' ? 'max-w-3xl' : 'max-w-lg'))
const sheetMaxW = computed(() => (props.size === 'lg' ? 'md:max-w-3xl' : 'md:max-w-lg'))

// Literal class strings (no interpolation) so Tailwind's scanner keeps
// them. Each level sits a layer above the previous; all below the
// global toast (z-[60]) and network banner (z-[70]).
const overlayZ = computed(() =>
  props.level === 'deep' ? 'z-[56]' : props.level === 'nested' ? 'z-[52]' : 'z-40',
)
const contentZ = computed(() =>
  props.level === 'deep' ? 'z-[58]' : props.level === 'nested' ? 'z-[54]' : 'z-50',
)

const contentClasses = computed(() =>
  cn(
    'fixed bg-card text-card-foreground shadow-lg outline-none',
    contentZ.value,
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'safe-bottom',
    props.variant === 'centered'
      ? [
          'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          // `overflow-auto` here is load-bearing: it clips the sticky
          // header/footer to the rounded corners (without it the bg-card
          // bleeds past `rounded-lg` and shows a flat top-right edge),
          // AND it gives `position: sticky` an actual scroll container
          // — otherwise the header/footer just sit in normal flow and
          // overlap each other when the body grows past the viewport.
          'max-h-[85vh] overflow-auto rounded-lg w-[calc(100%-2rem)]',
          centeredMaxW.value,
        ]
      : [
          // Mobile-first: bottom sheet
          'inset-x-0 bottom-0 max-h-[90vh] overflow-auto rounded-t-2xl',
          // Desktop: centred
          'md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
          'md:max-h-[85vh] md:w-full md:rounded-lg',
          sheetMaxW.value,
        ],
  ),
)
</script>

<template>
  <DialogRoot :open="open" @update:open="(v) => emit('update:open', v)">
    <DialogTrigger v-if="$slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay
        :class="
          cn(
            'fixed inset-0 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out',
            overlayZ,
          )
        "
      />
      <DialogContent
        :class="contentClasses"
        @escape-key-down="(e: KeyboardEvent) => !closable && e.preventDefault()"
        @interact-outside="(e: Event) => !closable && e.preventDefault()"
        @pointer-down-outside="(e: Event) => !closable && e.preventDefault()"
      >
        <!-- Header is sticky inside DialogContent's scroll area so the X
             and title stay reachable in long forms (the dish editor
             can easily run twice the sheet's height on mobile). The
             divider line under it only appears when something scrolls
             under (see `headerScrolled` + the sentinel below) — short
             dialogs stay borderless and feel clean. -->
        <!-- A Dialog MUST have an accessible name. The visible title
             lives in the header below, but the header only renders when
             there's a title/slot/close button — so a title-less,
             non-closable dialog (or one with only a close button) would
             otherwise have NO DialogTitle and Reka warns about a
             nameless dialog. Emit an sr-only fallback for exactly those
             cases. The two DialogTitle branches are mutually exclusive,
             so there's always exactly one. -->
        <DialogTitle v-if="!(title || $slots.title)" class="sr-only">Dialog</DialogTitle>

        <header
          v-if="title || $slots.title || closable"
          :class="
            cn(
              'sticky top-0 z-10 flex items-start gap-4 bg-card p-4 transition-shadow md:p-6',
              headerScrolled && 'border-b border-border shadow-sm',
            )
          "
        >
          <div class="flex-1 min-w-0">
            <DialogTitle
              v-if="title || $slots.title"
              :class="cn('text-lg font-semibold leading-tight', titleVisuallyHidden && 'sr-only')"
            >
              <slot name="title">{{ title }}</slot>
            </DialogTitle>
            <DialogDescription
              v-if="description || $slots.description"
              class="mt-1 text-sm text-muted-foreground"
            >
              <slot name="description">{{ description }}</slot>
            </DialogDescription>
          </div>
          <button
            v-if="closable"
            type="button"
            class="-mr-2 -mt-1 inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :aria-label="`Close ${title ?? 'dialog'}`"
            @click="emit('update:open', false)"
          >
            <X class="size-5" aria-hidden="true" />
          </button>
        </header>
        <div :class="cn('px-4 pb-4 md:px-6 md:pb-6', !(title || $slots.title) && 'pt-4 md:pt-6')">
          <!-- Sentinel: when this leaves the viewport the body has
               scrolled under the sticky header, and we flip the
               divider on. Zero-height so it adds no layout. -->
          <div ref="topSentinelRef" aria-hidden="true" class="h-0" />
          <slot />
          <!-- Companion sentinel just above the sticky footer. -->
          <div ref="bottomSentinelRef" aria-hidden="true" class="h-0" />
        </div>
        <!-- Footer also sticks to the bottom of the scroll area so
             Save/Cancel stay reachable. On mobile sheets (variant=sheet
             at <md) the sheet itself ends here, so static = sticky in
             practice; the rule earns its keep on the desktop centred
             panel + on tall content that pushes past the viewport. -->
        <footer
          v-if="$slots.footer"
          :class="
            cn(
              'sticky bottom-0 z-10 flex flex-col-reverse gap-2 bg-card p-4 md:flex-row md:justify-end md:p-6',
              footerScrolled && 'border-t border-border',
            )
          "
        >
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
